/**
 * useCrossChannelMemory — shared in-memory conversation log that survives
 * voice ↔ chat mode switches within the same widget shell instance.
 *
 * Lives at the `MultiChannelAssistantShell` level so both `VoiceModePanel`
 * and `ChatModePanel` read+write the same buffer. When the user switches
 * transports, the new transport seeds itself from this memory so the bot
 * continues exactly where it stopped, regardless of which mode produced
 * each prior turn. The handoff is invisible to the user: no greeting is
 * replayed, no context is lost.
 *
 * NOT persisted to storage. The buffer dies with the shell unmount. If
 * the user closes the widget panel and the shell unmounts, they start
 * fresh next time — which is the desired behavior (privacy + no stale
 * context across page reloads or long absences).
 *
 * Capped at MEMORY_CAP entries (FIFO) and MAX_CHARS_PER_MSG per message
 * to bound prompt size and Realtime API `conversation.item.create`
 * payload size when injecting prior context into voice mode.
 *
 * Codex hard rule #8 reminder: voice and chat are NEVER concurrent.
 * Memory is the only thing that crosses the boundary; the actual
 * transport sessions still mount/unmount mutually exclusively.
 */

import { useCallback, useMemo, useRef } from 'react';

export type ChannelMessageSource = 'voice' | 'chat';

export interface ChannelMessage {
  role: 'user' | 'assistant';
  content: string;
  source: ChannelMessageSource;
  ts: number;
}

const MEMORY_CAP = 30;
const MAX_CHARS_PER_MSG = 2000;

export interface CrossChannelMemory {
  /** Read-only snapshot. Always returns a fresh array copy. */
  snapshot(): ChannelMessage[];
  /**
   * Push one message. Trims content to MAX_CHARS_PER_MSG, enforces
   * MEMORY_CAP via FIFO eviction, and deduplicates consecutive identical
   * role+content pairs (common when the same transport emits both a
   * "final" and a "delta" copy of the same line).
   */
  append(msg: Omit<ChannelMessage, 'ts'> & { ts?: number }): void;
  /** True if there is at least one prior message in the buffer. */
  hasMemory(): boolean;
  /** Drop everything. Called when the user explicitly resets the session. */
  clear(): void;
}

export function useCrossChannelMemory(): CrossChannelMemory {
  const memoryRef = useRef<ChannelMessage[]>([]);

  const snapshot = useCallback((): ChannelMessage[] => {
    return memoryRef.current.slice();
  }, []);

  const append = useCallback(
    (raw: Omit<ChannelMessage, 'ts'> & { ts?: number }): void => {
      const content = (raw.content || '').trim();
      if (!content) return;
      const trimmed =
        content.length > MAX_CHARS_PER_MSG
          ? content.slice(0, MAX_CHARS_PER_MSG)
          : content;
      const last = memoryRef.current[memoryRef.current.length - 1];
      if (last && last.role === raw.role && last.content === trimmed) {
        return; // dedupe identical consecutive entries
      }
      const next: ChannelMessage = {
        role: raw.role,
        content: trimmed,
        source: raw.source,
        ts: raw.ts ?? Date.now(),
      };
      memoryRef.current.push(next);
      if (memoryRef.current.length > MEMORY_CAP) {
        memoryRef.current = memoryRef.current.slice(-MEMORY_CAP);
      }
    },
    [],
  );

  const hasMemory = useCallback(
    (): boolean => memoryRef.current.length > 0,
    [],
  );

  const clear = useCallback((): void => {
    memoryRef.current = [];
  }, []);

  return useMemo(
    () => ({ snapshot, append, hasMemory, clear }),
    [snapshot, append, hasMemory, clear],
  );
}
